// Shared duck-typing for the ApiError that useApi throws, and for the API's
// structured { error, message } failure body. Extracted from the byte-identical
// copies in Admin/Clients/_helpers/approve.ts,
// Customer/PaymentRequired/_helpers/paymentMethodError.ts and
// Customer/Shopping/Checkout/_helpers/checkoutOrderError.ts (rule of three).

// `useApi` throws an ApiError carrying `status` and the raw `detail` body.
// Duck-typed rather than imported so feature helpers stay free of the useApi
// module's config/MSAL imports (which Jest component tests mock away wholesale —
// an `instanceof ApiError` there would hit an undefined class).
export interface ApiFailure {
    status: number;
    detail?: string;
}

export const asApiFailure = (error: unknown): ApiFailure | undefined => {
    if (typeof error !== "object" || error === null || !("status" in error)) {
        return undefined;
    }

    const { status, detail } = error as { status: unknown; detail?: unknown };

    return typeof status === "number"
        ? {
            status,
            detail: typeof detail === "string" ? detail : undefined,
        }
        : undefined;
};

// The failures useApi has already shown the user: an HTTP or network failure
// it toasted, and the session-expired failure it warned about before the
// sign-in redirect. A caller that catches one of these need not rethrow it for
// it to be reported. Nothing else is in here: a failure whose toast was
// suppressed, a token failure useApi did not report, or a bug. Weak, so an
// error is forgotten with the error object.
const reportedFailures = new WeakSet<object>();

export const markReported = (error: unknown): void => {
    if (typeof error === "object" && error !== null) {
        reportedFailures.add(error);
    }
};

export const wasReported = (error: unknown): boolean =>
    typeof error === "object" && error !== null && reportedFailures.has(error);

// The API's structured failure envelope: a machine-readable `error` code plus
// customer-safe `message` copy authored by the API.
export interface ApiFailureBody {
    error: string;
    message: string;
}

// ApiError.detail is the raw body text. Returns undefined for anything that is
// not the documented { error, message } object, so callers fall back to their
// own copy. Wrong-typed fields parse to "" rather than leaking e.g. a literal
// "null" into customer copy.
export const parseFailureBody = (
    detail: string | undefined,
): ApiFailureBody | undefined => {
    if (!detail) {
        return undefined;
    }

    try {
        const parsed: unknown = JSON.parse(detail);

        if (typeof parsed !== "object" || parsed === null) {
            return undefined;
        }

        const { error, message } = parsed as Record<string, unknown>;

        return {
            error: typeof error === "string" ? error : "",
            message: typeof message === "string" ? message : "",
        };
    } catch {
        return undefined;
    }
};

// A failure body's fields when it is a JSON object, for coded bodies that carry
// more than { error, message } (#443's insufficient_stock: `available`,
// `items`). undefined for a sentence, an array, a scalar or no body.
export const parseJsonObject = (
    detail: string | undefined,
): Record<string, unknown> | undefined => {
    if (!detail) {
        return undefined;
    }

    try {
        const parsed: unknown = JSON.parse(detail);

        return typeof parsed === "object" &&
            parsed !== null &&
            !Array.isArray(parsed)
            ? (parsed as Record<string, unknown>)
            : undefined;
    } catch {
        return undefined;
    }
};

// The sentence of a plain-string failure body. An ObjectResult(string) reaches
// the client as text/plain or as a JSON string literal depending on which output
// formatter won; both are the same sentence and both are unwrapped. A body that
// parses to an object (problem+json, { error, message }) is no sentence and
// returns undefined, as does a blank one, so callers fall back to their own copy.
export const apiSentence = (detail: string | undefined): string | undefined => {
    const trimmed = detail?.trim();

    if (!trimmed) {
        return undefined;
    }

    try {
        const parsed: unknown = JSON.parse(trimmed);

        return typeof parsed === "string" && parsed.trim()
            ? parsed.trim()
            : undefined;
    } catch {
        return trimmed;
    }
};
